import Dce from "dce-engine/lib/decorators/Dce";
import {DceComponent, DceHTMLElement} from "dce-engine";
import {DceAjax} from "dce-ajax";

interface CraftvilleTableConfigProps {
    defaults: Record<string, string>;
    labels: Record<string, string>;
    submitUrl: string;
}

type StateCallback = (value: string, label: string) => void;

class StateItem {
    private value: string | null = null;
    private label: string | null = null;
    private callbacks: StateCallback[] = [];

    constructor(
        public readonly name: string
    ) {
    }

    public get(): string | null {
        return this.value;
    }

    public update(value: string, label: string): void {
        console.log('UPDATE', this.name, value);

        if (this.label != label) {
            this.label = label;
        }

        if (this.value === value) {
            return; // brak zmiany → brak callbacków
        }

        this.value = value;
        this.label = label;

        for (const cb of this.callbacks) {
            cb(this.value, this.label);
        }
    }

    public subscribe(cb: StateCallback): void {
        this.callbacks.push(cb);
    }
}

@Dce("CraftvilleTableConfig")
export default class CraftvilleTableConfig extends DceComponent<CraftvilleTableConfigProps> {

    private states: Map<string, StateItem> = new Map();

    private submitState: Map<string, string> = new Map();

    constructor(elem: DceHTMLElement, props: CraftvilleTableConfigProps) {
        super(elem, props);

        this.initStates();
        this.bindSummary();

        this.bindInputs();
        this.bindVisibility();

        this.applyDefaults();

        this.elem.style.opacity = null;

        this.elem.querySelector('[data-action="submit"]').addEventListener('click', e => {
            e.preventDefault();

            console.log("SUBMIT", this.submitState);

            let formdata = new FormData();
            for (const [key, value] of this.submitState) {
                formdata.append(key, value);
            }

            DceAjax.getInstance().post(this.props.submitUrl, formdata)
                .then(response => {
                    console.log("Submission response:", response);
                });


        })
    }

    private bindSummary() {
        const res = this.elem.querySelectorAll<HTMLElement>('[data-res]');

        res.forEach(el => {
            const name = el.dataset.res;
            if (!name) return;

            if (this.states.has(name)) {
                this.states.get(name)?.subscribe((value, label) => {


                    let stateLabel = name;
                    if (this.props.labels && this.props.labels[name]) {
                        stateLabel = this.props.labels[name];
                    }
                    this.submitState.set(name, stateLabel + ": " + label.trim() + " [" + value.trim() + "]");

                    console.log("SUMMARY:", this.submitState);

                    const v = el.querySelector('[data-role="value"]');
                    if (!v) {
                        return;
                    }
                    v.innerHTML = label;
                })
            }
        })

    }

    private applyDefaults(): void {
        const defaults = this.props.defaults;
        if (!defaults) return;

        let entries: [string, string][] = [];

        // 🔹 Map
        if (defaults instanceof Map) {
            entries = Array.from(defaults.entries());
        }
        // 🔹 Plain object
        else if (typeof defaults === 'object') {
            entries = Object.entries(defaults);
        }
        // 🔹 coś dziwnego
        else {
            console.warn('Invalid defaults type', defaults);
            return;
        }

        for (const [stateName, value] of entries) {
            this.applyDefault(stateName, String(value));
        }
    }

    private applyDefault(stateName: string, value: string): void {
        const state = this.states.get(stateName);
        if (!state) return;

        this.syncDomWithState(stateName, value);
        state.update(value, this.getValueLabel(stateName, value));

    }


    private getValueLabel(stateName: string, value: string): string {

        let stateinput = document.querySelector<HTMLElement>('[data-state="' + stateName + '"]');

        if (!stateinput || !stateinput.dataset.input) {
            return '';
        }

        if (stateinput.dataset.input == 'radiolist') {
            let radio = stateinput.querySelector<HTMLInputElement>('input[type="radio"][value="' + value + '"]');
            if (radio) {
                let labelElem = radio.closest('label').querySelector('[data-label-for]');
                return labelElem ? labelElem.textContent : '';
            }
        }

        if (stateinput.dataset.input == 'checkbox') {
            let labelElem = stateinput.querySelector('[data-label-for]');
            return labelElem ? labelElem.textContent : '';
        }

        if (stateinput.dataset.input == 'slider') {
            let inputElement = stateinput.querySelector<HTMLInputElement>('input[type="range"]');
            return inputElement ? inputElement.value + ' cm' : '';
        }

        return '';
    }

    private syncDomWithState(stateName: string, value: string): void {
        // RADIO
        const radios = this.elem.querySelectorAll<HTMLInputElement>(
            `input[type="radio"][name="${stateName}"]`
        );

        radios.forEach(radio => {
            radio.checked = radio.value === value;
        });

        // CHECKBOX
        const checkbox = this.elem.querySelector<HTMLInputElement>(
            `input[type="checkbox"][name="${stateName}"]`
        );

        if (checkbox) {
            checkbox.checked = checkbox.value === value;
        }

        // SLIDER
        const slider = this.elem.querySelector<HTMLInputElement>(
            `input[type="range"][name="${stateName}"]`
        );

        if (slider) {
            slider.value = value;
            // 🔥 ważne: trigger update UI (np. label z wartością)
            slider.dispatchEvent(new Event('input', {bubbles: true}));
        }
    }

    // 🔹 1. Tworzymy state na podstawie DOM
    private initStates(): void {
        const elements = this.elem.querySelectorAll<HTMLElement>('[data-state]');

        elements.forEach(el => {
            const name = el.dataset.state;
            if (!name) return;

            if (!this.states.has(name)) {
                this.states.set(name, new StateItem(name));
            }
        });
    }

    // 🔹 2. Podpinamy inputy
    private bindInputs(): void {
        const inputs = this.elem.querySelectorAll<HTMLElement>('[data-input]');

        inputs.forEach(wrapper => {
            const type = wrapper.dataset.input;

            switch (type) {
                case 'radiolist':
                    this.bindRadioList(wrapper);
                    break;

                case 'checkbox':
                    this.bindCheckbox(wrapper);
                    break;

                case 'slider':
                    this.bindSlider(wrapper);
                    break;
            }
        });
    }

    // RADIO
    private bindRadioList(wrapper: HTMLElement): void {
        const inputs = wrapper.querySelectorAll<HTMLInputElement>('input[type="radio"]');

        inputs.forEach(input => {
            input.addEventListener('change', () => {
                if (!input.checked) return;

                const state = this.states.get(input.name);
                if (!state) return;

                let labelElem = input.closest('label').querySelector('[data-label-for]');

                state.update(input.value, labelElem ? labelElem.textContent : '');
            });
        });
    }

    // CHECKBOX
    private bindCheckbox(wrapper: HTMLElement): void {
        const input = wrapper.querySelector<HTMLInputElement>('input[type="checkbox"]');
        if (!input) return;

        input.addEventListener('change', () => {
            const state = this.states.get(input.name);
            if (!state) return;

            let labelElem = input.closest('label').querySelector('[data-label-for]');

            if (input.checked) {
                state.update(input.value, labelElem ? labelElem.textContent : '');
            } else {
                state.update('', ''); // brak wartości
            }
        });
    }

    // SLIDER
    private bindSlider(wrapper: HTMLElement): void {
        const input = wrapper.querySelector<HTMLInputElement>('input[type="range"]');
        if (!input) return;

        input.addEventListener('input', () => {
            const state = this.states.get(input.name);
            if (!state) return;

            let labelElem = input.closest('[data-input]').querySelector('[data-label-for]');

            state.update(input.value, input.value + ' cm');
        });
    }

    private bindVisibility(): void {
        const elements = this.elem.querySelectorAll<HTMLElement>('[data-visible]');

        elements.forEach(el => {
            const raw = el.dataset.visible;

            let visibleMap: Record<string, string[]> = {};

            try {
                visibleMap = raw ? JSON.parse(raw) : {};
            } catch (e) {
                console.warn('Invalid data-visible JSON', raw);
                visibleMap = {};
            }

            // 🔹 subskrybujemy wszystkie zależne state
            Object.keys(visibleMap).forEach(stateName => {
                const state = this.states.get(stateName);
                if (!state) return;

                state.subscribe(() => {
                    console.log('UPDATE VISIBILITY');
                    this.updateElementVisibility(el, visibleMap);
                });
            });

            // 🔹 initial render
            this.updateElementVisibility(el, visibleMap);
        });
    }

    private updateElementVisibility(el: HTMLElement, visibleMap: Record<string, string[]>) {
        const isVisible = this.matchesVisible(visibleMap, this.states);
        el.style.display = isVisible ? '' : 'none';
    }

    private matchesVisible(visible: Record<string, string[]>, states: Map<string, StateItem>): boolean {
        // brak warunków = zawsze widoczne
        if (!visible || Object.keys(visible).length === 0) {
            return true;
        }

        for (const [stateName, allowedValues] of Object.entries(visible)) {
            const state = states.get(stateName);

            if (!state) {
                return false;
            }

            const currentValue = state.get();

            // brak wartości → nie spełnia warunku
            if (!currentValue) {
                return false;
            }

            // OR: value musi być w liście
            if (!allowedValues.includes(currentValue)) {
                return false;
            }
        }

        return true;
    }
}